import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import equals from 'ramda/es/equals';
import { steppedHPConfigurations, clinicalStepsBaseline, getStepsFromVersioned } from '../state';
import type { StepConfiguration } from '../steppedHPTypes';

/**
 * Deep-compare two steps on the fields that can change in clinical mode.
 * Exported for testing.
 */
export function isStepEqual(
  a: Partial<StepConfiguration> | undefined,
  b: Partial<StepConfiguration> | undefined
): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return (
    a.display_name === b.display_name &&
    equals(a.layouts, b.layouts) &&
    equals(a.config_details, b.config_details) &&
    equals(a.viewports, b.viewports)
  );
}

/**
 * Folds a freshly fetched step list into the local one, keeping any step that currently holds
 * unsaved edits.
 *
 * A refetch used to replace the step atom wholesale, so any HP refetch -- including the one the
 * other viewer window performs when it receives a cross-window HP invalidation, which a step reorder
 * triggers -- silently threw away this window's unsaved step edits (VX-4739). Order and membership
 * come from the server; content comes from the server except for steps whose local copy differs from
 * the local baseline, i.e. the ones the user has edited but not saved.
 *
 * Steps are matched by smid where possible, but a reorder does not preserve step identity at all:
 * `updateHangingProtocol` deletes a HP's whole step collection and recreates it
 * (`HangingProtocolSerializer.update` in backend-api), and the nested step serializer treats `smid`
 * as read-only, so the steps that come back from the refetch carry brand new smids. Our own GraphQL
 * layer also strips `smid` on the way out (`transformStepPayload`), so the backend never sees the
 * existing ids either. Tracked in VX-4741. When none of the fetched smids are recognised and the
 * step count is unchanged, steps are re-identified by position instead -- the server order is the
 * order we just sent it -- and a preserved local edit adopts the fetched smid so a later save
 * targets the row that now exists.
 */
export function mergeUnsavedStepEdits(
  fetchedSteps: StepConfiguration[] | null,
  previousSteps: Nullable<StepConfiguration[]>,
  previousBaseline: Nullable<StepConfiguration[]>
): StepConfiguration[] | null {
  if (fetchedSteps == null || previousSteps == null || previousBaseline == null)
    return fetchedSteps;

  const previousBySmid = new Map(
    previousSteps.filter((step) => step.smid != null).map((step) => [step.smid, step])
  );
  const baselineBySmid = new Map(
    previousBaseline.filter((step) => step.smid != null).map((step) => [step.smid, step])
  );

  const anySmidRecognised = fetchedSteps.some(
    (step) => step.smid != null && previousBySmid.has(step.smid)
  );
  // Positional fallback is only safe when the server handed back a same-sized set it has re-keyed.
  // A differing count means steps were genuinely added or removed, where position says nothing.
  const matchByPosition =
    !anySmidRecognised &&
    fetchedSteps.length === previousSteps.length &&
    fetchedSteps.length === previousBaseline.length;

  return fetchedSteps.map((fetchedStep, index) => {
    const localStep = matchByPosition
      ? previousSteps[index]
      : fetchedStep.smid != null
        ? previousBySmid.get(fetchedStep.smid)
        : undefined;
    const baselineStep = matchByPosition
      ? previousBaseline[index]
      : fetchedStep.smid != null
        ? baselineBySmid.get(fetchedStep.smid)
        : undefined;

    if (localStep == null || baselineStep == null) return fetchedStep;
    if (isStepEqual(localStep, baselineStep)) return fetchedStep;
    // Keep the unsaved edit, but on the identity the server actually has.
    return { ...localStep, smid: fetchedStep.smid };
  });
}

/** Returns per-step dirty state by comparing current steps against the baseline snapshot. */
export function useClinicalStepDirtyState() {
  const currentVersioned = useRecoilValue(steppedHPConfigurations);
  const current = getStepsFromVersioned(currentVersioned);
  const baseline = useRecoilValue(clinicalStepsBaseline);

  const dirtyIndices = useMemo(() => {
    if (!current || !baseline) return new Set<number>();
    // Match steps by smid rather than by index so that add/delete/reorder
    // of OTHER steps doesn't create false dirty indicators on unrelated
    // steps due to index misalignment (e.g., after a delete+undo cycle
    // where the baseline was eagerly filtered but the step reappeared).
    const baselineBySmid = new Map(
      baseline.filter((s) => s.smid != null).map((s) => [s.smid as string, s])
    );
    const dirty = new Set<number>();
    for (let i = 0; i < current.length; i++) {
      const currentStep = current[i];
      // Steps without a smid (newly added, not yet persisted) are never
      // dirty — they exist only in the current atom, not the baseline.
      if (currentStep.smid == null) continue;
      const baselineStep = baselineBySmid.get(currentStep.smid);
      // If the baseline has no entry for this smid, the step is new
      // (pending creation) — treat as clean rather than dirty.
      if (baselineStep == null) continue;
      if (!isStepEqual(currentStep, baselineStep)) {
        dirty.add(i);
      }
    }
    return dirty;
  }, [current, baseline]);

  return {
    dirtyIndices,
    isStepDirty: (i: number) => dirtyIndices.has(i),
    hasAnyDirtyStep: dirtyIndices.size > 0,
  };
}
